import { Form } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addUser,
  GetUserByDepartmentId,
  getUserById,
  updateUser,
} from "../../api/UserApi";
import { UserForm } from "./UserForm";
import moment from "moment";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import { getDepartments } from "../../api/DepartmentApi";
import { getPositions } from "../../api/PositionApi";
import { getRoles } from "../../api/RoleApi";
import { tokenKey } from "../../api/OAuth";
import {
  userHasRolePermission,
  userHasRolePermissionWithToken,
  userRoleFromToken,
} from "../../api/ApiUtils";
import {
  appTitle,
  dateFormat,
  Departement,
  RolesDescription,
} from "../common/GeneralConfigurations";
import { getWorkPlaces } from "../../api/WorkPlaceApi";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import { AppRoutes } from "../../AppRoutes";
import { useTranslation } from "react-i18next";

export const ManageUserPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [workPlaces, setWorkPlaces] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [managers, setManagers] = useState([]);
  const [initValues, setInitValues] = useState({
    name: "",
    email: "",
    phone: "",
    departmentId: null,
    departmentName: null,
    rolesIds: [],
    positionId: null,
    positionName: null,
    code: "",
    seniorManagerId: null,
    seniorManagerName: null,
    birthDate: null,
    workPlaceId: 1,
    workPlaceName: null,
    status: null,
  });
  const title =
    id !== undefined ? t("user.pages.editUser") : t("user.pages.newUser");
  const isRH = userHasRolePermission(RolesDescription.HumanResources);
  const userRole = userRoleFromToken();

  const [readOnly, setReadOnly] = useState(false);
  const [defaultPickerValue, setDefaultPickerValue] = useState(
    moment.utc("1995-01-01", dateFormat)
  );
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    document.title = `${appTitle} - ${title}`;
  }, [title]);

  const disabledDate = (current) => {
    if (
      moment().year() - current.year() < 18 ||
      moment().year() - current.year() > 120
    )
      return true;
    if (moment().year() - current.year() === 18) {
      if (moment().month() < current.month()) return true;
      if (moment().month() === current.month()) {
        if (moment().date() < current.date()) return true;
      }
    }
    return false;
  };

  const handleCancel = () => {
    navigate(AppRoutes.userList.path);
  };

  const fetchUserData = useCallback(async () => {
    try {
      const res = await getUserById(id);
      const {
        name,
        email,
        phone,
        invalidPhone,
        departmentId,
        departmentName,
        positionId,
        positionName,
        roles,
        code,
        seniorManagerId,
        seniorManagerName,
        birthDate,
        deleted,
        workPlaceId,
        workPlaceName,
      } = res.data;
      const bDate =
        birthDate === null ? null : moment.utc(birthDate, "yyyy-MM-DD");

      setInitValues({
        name,
        email,
        phone,
        invalidPhone,
        departmentId,
        departmentName,
        positionId,
        positionName,
        rolesIds: roles.map((role) => role.id),
        code,
        seniorManagerId,
        seniorManagerName,
        birthDate: bDate,
        status: deleted ? t("user.status.inactive") : t("user.status.active"),
        workPlaceId,
        workPlaceName,
      });
      bDate && setDefaultPickerValue(bDate);
    } catch (e) {
      navigate(AppRoutes.userList.path);
      AdnMessageError();
      return;
    }
  }, [id, t, navigate]);

  const fetchPositions = async () => {
    const res = await getPositions();
    setPositions(res.data);
  };

  const fetchDepartments = async () => {
    const res = await getDepartments();
    setDepartments(res.data);
  };

  const fetchWorkPlaces = async () => {
    const res = await getWorkPlaces();
    setWorkPlaces(res.data.filter((wp) => wp.isInternal));
  };

  const fetchRoles = async () => {
    const res = await getRoles();
    setRoleOptions(res.data);
  };

  const fetchManagers = async () => {
    const res = await GetUserByDepartmentId(Departement.Manager);
    setManagers(res.data.result.filter((u) => !u.deleted));
  };

  useEffect(() => {
    const fetchArray = [
      fetchPositions(),
      fetchDepartments(),
      fetchWorkPlaces(),
      fetchManagers(),
      fetchRoles(),
    ];
    if (id !== undefined) {
      const token = localStorage.getItem(tokenKey);
      if (!userHasRolePermissionWithToken(RolesDescription.Admin, token)) {
        setReadOnly(true);
      }
      fetchArray.push(fetchUserData());
    }
    Promise.all(fetchArray)
      .then(() => setLoading(false))
      .catch((err) => console.log(err));
  }, [fetchUserData, id]);

  useEffect(() => {
    form.resetFields();
  }, [initValues, form]);

  const handleFinish = (values) => {
    id === undefined ? add(valuesToUser(values)) : update(valuesToUser(values));
  };

  const valuesToUser = (values) => ({
    ...values,
    deleted: values.status === t("user.status.inactive") ? true : false,
  });

  const add = async (newUser) => {
    setSubmitLoading(true);
    const res = await addUser(newUser);
    if (res.status === 201) {
      navigate(AppRoutes.userList.path);
      AdnMessageSuccess(t("user.success.userAdded"));
      setSubmitLoading(false);
      return;
    }
    if (res.status === 400) {
      form.setFields(
        res.data.errors.map((e) => ({
          name: e.message.split(" ")[0].toLowerCase(),
          errors: [e.message],
        }))
      );
      setSubmitLoading(false);
      return;
    }
    setSubmitLoading(false);
    AdnMessageError(t("user.errors.errorAddingUser"));
  };

  const update = async (updatedUser) => {
    setSubmitLoading(true);

    if (updatedUser.status === t("user.status.inactive")) {
      updatedUser.deleted = true;
    }

    const res = await updateUser(id, updatedUser);
    if (res.status === 200) {
      navigate(AppRoutes.userList.path);
      AdnMessageSuccess(t("user.success.userUpdated"));
      setSubmitLoading(false);
      return;
    } else if (res.data.errors) {
      res.data.errors.forEach((e) => {
        AdnMessageError(e.message);
      });
    }

    setSubmitLoading(false);
    if (res.status === 400) {
      form.setFields(
        res.data.errors.map((e) => ({
          name: e.message.split(" ")[0].toLowerCase(),
          errors: [e.message],
        }))
      );
      setSubmitLoading(false);
      return;
    }
    setSubmitLoading(false);
    AdnMessageError(t("user.errors.errorUpdatingUser"));
  };

  const handleBlurName = () => {
    form.setFieldsValue({
      name: form
        .getFieldValue("name")
        ?.trim()
        .split(/\s+/)
        .filter((w) => w !== "")
        .map(
          ([firstChar, ...rest]) =>
            firstChar.toUpperCase() + rest.join("").toLowerCase()
        )
        .join(" "),
    });
    form.validateFields(["name"]);
  };

  const handleBlurEmail = () => {
    form.setFieldsValue({
      email: form.getFieldValue("email")?.trim().toLowerCase(),
    });
    form.validateFields(["email"]);
  };

  return (
    <UserForm
      form={form}
      departments={departments}
      positions={positions}
      workPlaces={workPlaces}
      managers={managers}
      roles={roleOptions}
      initValues={initValues}
      readOnly={readOnly}
      disabledDate={disabledDate}
      defaultPickerValue={defaultPickerValue}
      loading={loading}
      submitLoading={submitLoading}
      title={title}
      handleCancel={handleCancel}
      handleFinish={handleFinish}
      handleBlurName={handleBlurName}
      handleBlurEmail={handleBlurEmail}
      isRH={isRH}
      userRole={userRole}
    />
  );
};
